import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { VoucherTextIssue, VoucherTextLanguage, VoucherTextScope } from '@bandido/shared/types';

import { VoucherTextsDraft, voucherTextLimit, voucherVerticalRows } from '../voucherTexts';
import { TextBlockBox } from './TextBlockBox';

const KEY = 'vouchers.config';

interface VerticalsSectionProps {
  draft: VoucherTextsDraft;
  /** Los topes que publica el `GET`: el mismo número que rechaza el backend. */
  limits?: Record<string, number>;
  issues: Record<string, VoucherTextIssue[]>;
  /** Un rechazo que no cayó en ninguna fila: se dice, no se traga. */
  otrosRechazados?: boolean;
  canEdit: boolean;
  /** Por qué no se puede editar, cuando no se puede. */
  disabledReason?: string;
  /** El vertical abierto, o ninguno. Lo maneja la pantalla porque mueve la vista previa. */
  open?: string;
  onOpen: (vertical: string | undefined) => void;
  onChange: (scope: VoucherTextScope, key: string, lang: VoucherTextLanguage, body: string) => void;
}

/**
 * POR TIPO DE PRODUCTO: un texto para los traslados, otro para los tours, otro
 * para los autos. Se suma al de la agencia, no lo reemplaza.
 *
 * Los NUEVE se listan siempre, tengan texto o no: la lista es también la
 * respuesta a "¿y los demás?" —el que no tiene propio usa el de la agencia—, y
 * esconderlos obligaría a recordar cuáles faltan.
 *
 * El editor se abre DEBAJO de la fila y no en un modal (W2): el asesor escribe
 * mirando la vista previa de ese mismo producto, que es lo que hace que el
 * texto se entienda en su lugar.
 */
export const VerticalsSection: FC<VerticalsSectionProps> = ({
  draft,
  limits,
  issues,
  otrosRechazados,
  canEdit,
  disabledReason,
  open,
  onOpen,
  onChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const rows = voucherVerticalRows(draft, issues);

  return (
    <Stack gap={2}>
      <Alert severity="info">{t(`${KEY}.byVertical.appliesTo`)}</Alert>

      {!canEdit && disabledReason && <Alert severity="warning">{t(disabledReason)}</Alert>}
      {/* El MISMO cartel que la sección de textos: un rechazo que no se puede
          ubicar en una fila tiene que decirse desde donde el asesor está. */}
      {otrosRechazados && <Alert severity="error">{t(`${KEY}.texts.otherRejected`)}</Alert>}

      <Stack gap={1}>
        {rows.map((row) => {
          const abierto = open === row.key;
          return (
            <Box
              key={row.key}
              sx={{
                border: `1px solid ${row.hasIssues ? theme.palette.error.main : theme.palette.divider}`,
                borderRadius: '12px',
                padding: theme.spacing(1.5, 2),
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                sx={{ cursor: 'pointer' }}
                onClick={() => onOpen(abierto ? undefined : row.key)}
              >
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle2">{t(`rutero.verticals.${row.key}`)}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {/* La primera línea de lo escrito, para reconocerlo sin abrir. */}
                    {row.own ? row.preview : t(`${KEY}.byVertical.agencyOnly`)}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={row.own ? 'primary' : 'default'}
                  variant={row.own ? 'filled' : 'outlined'}
                  label={t(`${KEY}.byVertical.${row.own ? 'hasOwn' : 'usesAgency'}`)}
                />
                <IconButton size="small" aria-label={t(`${KEY}.byVertical.${abierto ? 'close' : 'edit'}`)}>
                  {abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={abierto} unmountOnExit>
                <Box pt={2}>
                  <TextBlockBox
                    scope="vertical"
                    blockKey={row.key}
                    draft={draft}
                    limit={voucherTextLimit(limits, 'vertical', row.key)}
                    issues={issues}
                    canEdit={canEdit}
                    hint={t(`${KEY}.byVertical.hint`)}
                    onChange={onChange}
                  />
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};
